import L from 'leaflet';
import RBush from 'rbush';

  var markersCanvas = {
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // private: properties
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    _map: null,
    _canvas: null,
    _context: null,

    // leaflet markers (used to getBounds)
    _markers: [],
    _addedMarkerIds: new Set(),

    // visible markers
    _markersTree: new RBush(),

    // every marker positions (even out of the canvas)
    _positionsTree: new RBush(),

    // icon images index
    _icons: {},

    // marker currently being dragged.  Markers rendered by this layer do not
    // have a DOM icon, so Leaflet's built-in MarkerDrag handler has nothing to
    // attach to.  The layer handles that interaction on the canvas instead.
    _draggedMarker: null,
    _dragMoved: false,
    _suppressClick: false,
    _clickSuppressTimeout: null,

    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // public: global
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    addTo: function addTo(map) {
      map.addLayer(this);

      return this;
    },

    getBounds: function getBounds() {
      var bounds = new L.LatLngBounds();

      this._markers.forEach(function (marker) {
        bounds.extend(marker.getLatLng());
      });

      return bounds;
    },

    redraw: function redraw() {
      this._redraw(true);
    },

    clear: function clear() {
      this._positionsTree = new RBush();
      this._markersTree = new RBush();
      this._markers = [];
      this._addedMarkerIds.clear();
      this._redraw(true);
    },

    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // public: markers
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    addMarker: function addMarker(marker) {
      var ref = this._addMarker(marker);
      var markerBox = ref.markerBox;
      var positionBox = ref.positionBox;
      var isVisible = ref.isVisible;

      if (markerBox && isVisible) {
        this._markersTree.insert(markerBox);
      }

      if (positionBox) {
        this._positionsTree.insert(positionBox);
      }
    },

    // add multiple markers (better for rBush performance)
    addMarkers: function addMarkers(markers) {
      var this$1 = this;

      var markerBoxes = [];
      var positionBoxes = [];

      markers.forEach(function (marker) {
        var ref = this$1._addMarker(marker);
        var markerBox = ref.markerBox;
        var positionBox = ref.positionBox;
        var isVisible = ref.isVisible;

        if (markerBox && isVisible) {
          markerBoxes.push(markerBox);
        }

        if (positionBox) {
          positionBoxes.push(positionBox);
        }
      });

      this._markersTree.load(markerBoxes);
      this._positionsTree.load(positionBoxes);
    },

    removeMarker: function removeMarker(marker) {
      var latLng = marker.getLatLng();
      var isVisible = this._map.getBounds().contains(latLng);

      var positionBox = {
        minX: latLng.lng,
        minY: latLng.lat,
        maxX: latLng.lng,
        maxY: latLng.lat,
        marker: marker,
      };

      this._positionsTree.remove(positionBox, function (a, b) {
        return a.marker._leaflet_id === b.marker._leaflet_id;
      });
      this._addedMarkerIds.delete(marker._leaflet_id);
      this._markers = this._markers.filter(function (item) { return item._leaflet_id !== marker._leaflet_id; });

      if (isVisible) {
        this._redraw(true);
      }
    },

    // Update a marker and its geographic index together. Markers rendered by
    // this layer are not Leaflet layers, so Marker.setLatLng() alone leaves
    // the position index at the old coordinates. Keep hidden markers hidden.
    repositionMarker: function repositionMarker(marker, latLng) {
      var wasAdded = this._addedMarkerIds.has(marker._leaflet_id);

      if (wasAdded) {
        this.removeMarker(marker);
      }

      marker.setLatLng(latLng);

      if (wasAdded) {
        this.addMarker(marker);
      }

      this._redraw(true);
    },

    // remove multiple markers (better for rBush performance)
    removeMarkers: function removeMarkers(markers) {
      var this$1 = this;

      var hasChanged = false;

      markers.forEach(function (marker) {
        var latLng = marker.getLatLng();
        var isVisible = this$1._map.getBounds().contains(latLng);

        var positionBox = {
          minX: latLng.lng,
          minY: latLng.lat,
          maxX: latLng.lng,
          maxY: latLng.lat,
          marker: marker,
        };

        this$1._positionsTree.remove(positionBox, function (a, b) {
          return a.marker._leaflet_id === b.marker._leaflet_id;
        });
        this$1._addedMarkerIds.delete(marker._leaflet_id);
        this$1._markers = this$1._markers.filter(function (item) { return item._leaflet_id !== marker._leaflet_id; });

        if (isVisible) {
          hasChanged = true;
        }
      });

      if (hasChanged) {
        this._redraw(true);
      }
    },

    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // leaflet: default methods
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    initialize: function initialize(options) {
      L.Util.setOptions(this, options);
    },

    // called by Leaflet on `map.addLayer`
    onAdd: function onAdd(map) {
      this._map = map;
      this._initCanvas();
      this.getPane().appendChild(this._canvas);

      map.on("moveend", this._reset, this);
      map.on("resize", this._reset, this);

      map.on("click", this._fire, this);
      map.on("mousemove", this._fire, this);

      // The canvas needs to receive pointer events in order to stop a marker
      // drag from also starting a map drag.  Events which are not on a marker
      // still bubble to the map and are handled as before.
      this._canvas.style.pointerEvents = "auto";
      L.DomEvent.on(this._canvas, "mousedown touchstart", this._onPointerDown, this);
      L.DomEvent.on(this._canvas, "click", this._onCanvasClick, this);

      if (map._zoomAnimated) {
        map.on("zoomanim", this._animateZoom, this);
      }
    },

    // called by Leaflet
    onRemove: function onRemove(map) {
      this.getPane().removeChild(this._canvas);

      map.off("click", this._fire, this);
      map.off("mousemove", this._fire, this);
      map.off("moveend", this._reset, this);
      map.off("resize", this._reset, this);

      L.DomEvent.off(this._canvas, "mousedown touchstart", this._onPointerDown, this);
      L.DomEvent.off(this._canvas, "click", this._onCanvasClick, this);
      this._stopDragging(null, false);
      clearTimeout(this._clickSuppressTimeout);
      this._clickSuppressTimeout = null;

      if (map._zoomAnimated) {
        map.off("zoomanim", this._animateZoom, this);
      }
    },

    setOptions: function setOptions(options) {
      L.Util.setOptions(this, options);

      return this.redraw();
    },

    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // private: global methods
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    _initCanvas: function _initCanvas() {
      var ref = this._map.getSize();
      var x = ref.x;
      var y = ref.y;
      var isAnimated = this._map.options.zoomAnimation && L.Browser.any3d;

      this._canvas = L.DomUtil.create(
        "canvas",
        "leaflet-markers-canvas-layer leaflet-layer"
      );
      this._canvas.width = x;
      this._canvas.height = y;
      this._context = this._canvas.getContext("2d");

      L.DomUtil.addClass(
        this._canvas,
        ("leaflet-zoom-" + (isAnimated ? "animated" : "hide"))
      );
    },

    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // private: marker methods
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    _addMarker: function _addMarker(marker) {
      if (marker.options.pane !== "markerPane" || !marker.options.icon) {
        console.error("This is not a marker", marker);

        return { markerBox: null, positionBox: null, isVisible: null };
      }

      // required for pop-up and tooltip
      marker._map = this._map;

      // add _leaflet_id property
      L.Util.stamp(marker);

      if (this._addedMarkerIds.has(marker._leaflet_id)) {
        return { markerBox: null, positionBox: null, isVisible: false, added: false };
      }
      this._addedMarkerIds.add(marker._leaflet_id);

      var latLng = marker.getLatLng();
      var isVisible = this._map.getBounds().contains(latLng);
      var ref = this._map.latLngToContainerPoint(latLng);
      var x = ref.x;
      var y = ref.y;
      var ref$1 = marker.options.icon.options;
      var iconSize = ref$1.iconSize;
      var iconAnchor = ref$1.iconAnchor;

      var markerBox = {
        minX: x - iconAnchor[0],
        minY: y - iconAnchor[1],
        maxX: x + iconSize[0] - iconAnchor[0],
        maxY: y + iconSize[1] - iconAnchor[1],
        marker: marker,
      };

      var positionBox = {
        minX: latLng.lng,
        minY: latLng.lat,
        maxX: latLng.lng,
        maxY: latLng.lat,
        marker: marker,
      };

      if (isVisible) {
        this._drawMarker(marker, { x: x, y: y });
      }

      this._markers.push(marker);

      return { markerBox: markerBox, positionBox: positionBox, isVisible: isVisible, added: true };
    },

    _drawMarker: function _drawMarker(marker, ref) {
      var this$1 = this;
      var x = ref.x;
      var y = ref.y;

      var ref$1 = marker.options.icon.options;
      var iconUrl = ref$1.iconUrl;

      if (marker.image) {
        this._drawImage(marker, { x: x, y: y });
      } else if (this._icons[iconUrl]) {
        marker.image = this._icons[iconUrl].image;

        if (this._icons[iconUrl].isLoaded) {
          this._drawImage(marker, { x: x, y: y });
        } else {
          this._icons[iconUrl].elements.push({ marker: marker, x: x, y: y });
        }
      } else {
        var image = new Image();
        image.src = iconUrl;
        marker.image = image;

        this._icons[iconUrl] = {
          image: image,
          isLoaded: false,
          elements: [{ marker: marker, x: x, y: y }],
        };

        image.onload = function () {
          this$1._icons[iconUrl].isLoaded = true;
          this$1._icons[iconUrl].elements.forEach(function (ref) {
            var marker = ref.marker;
            var x = ref.x;
            var y = ref.y;

            this$1._drawImage(marker, { x: x, y: y });
          });
        };
      }
    },

    _drawImage: function _drawImage(marker, ref) {
      var x = ref.x;
      var y = ref.y;

      var ref$1 = marker.options.icon.options;
      var rotationAngle = ref$1.rotationAngle;
      var iconAnchor = ref$1.iconAnchor;
      var iconSize = ref$1.iconSize;
      var angle = rotationAngle || 0;

      this._context.save();
      this._context.translate(x, y);
      this._context.rotate((angle * Math.PI) / 180);
      this._context.drawImage(
        marker.image,
        -iconAnchor[0],
        -iconAnchor[1],
        iconSize[0],
        iconSize[1]
      );
      this._context.restore();
    },

    _redraw: function _redraw(clear) {
      var this$1 = this;

      if (clear) {
        this._context.clearRect(0, 0, this._canvas.width, this._canvas.height);
      }

      if (!this._map || !this._positionsTree) { return; }

      var mapBounds = this._map.getBounds();
      var mapBoundsBox = {
        minX: mapBounds.getWest(),
        minY: mapBounds.getSouth(),
        maxX: mapBounds.getEast(),
        maxY: mapBounds.getNorth(),
      };

      // draw only visible markers
      var markers = [];
      this._positionsTree.search(mapBoundsBox).forEach(function (ref) {
        var marker = ref.marker;

        var latLng = marker.getLatLng();
        var ref$1 = this$1._map.latLngToContainerPoint(latLng);
        var x = ref$1.x;
        var y = ref$1.y;
        var ref$2 = marker.options.icon.options;
        var iconSize = ref$2.iconSize;
        var iconAnchor = ref$2.iconAnchor;

        var markerBox = {
          minX: x - iconAnchor[0],
          minY: y - iconAnchor[1],
          maxX: x + iconSize[0] - iconAnchor[0],
          maxY: y + iconSize[1] - iconAnchor[1],
          marker: marker,
        };

        markers.push(markerBox);
        this$1._drawMarker(marker, { x: x, y: y });
      });

      this._markersTree.clear();
      this._markersTree.load(markers);
    },

    _markerAt: function _markerAt(containerPoint) {
      if (!this._markersTree) { return null; }

      var markers = this._markersTree.search({
        minX: containerPoint.x,
        minY: containerPoint.y,
        maxX: containerPoint.x,
        maxY: containerPoint.y,
      });

      return markers && markers.length ? markers[0].marker : null;
    },

    _isMarkerDraggable: function _isMarkerDraggable(marker) {
      // Respect marker.dragging.disable() when the standard Leaflet handler is
      // present.  (It cannot perform the drag itself because this layer has no
      // marker icon in the DOM.)  This also supports an application enabling
      // dragging later with marker.dragging.enable().
      if (marker.dragging) {
        return marker.dragging.enabled();
      }

      return !!marker.options.draggable;
    },

    _eventToContainerPoint: function _eventToContainerPoint(event) {
      // mouseEventToContainerPoint expects mouse-like coordinates.  For touch
      // events use the active touch (or the touch that just ended).
      var source = event.touches && event.touches.length
        ? event.touches[0]
        : (event.changedTouches && event.changedTouches.length
          ? event.changedTouches[0]
          : event);

      return this._map.mouseEventToContainerPoint(source);
    },

    _onPointerDown: function _onPointerDown(event) {
      if (this._draggedMarker || !this._map) { return; }

      var containerPoint = this._eventToContainerPoint(event);
      var marker = this._markerAt(containerPoint);

      if (!marker || !this._isMarkerDraggable(marker)) { return; }

      // This handler runs on the canvas before Leaflet's map handlers on its
      // parent.  Stopping the event prevents the map from panning while a
      // marker is dragged.
      L.DomEvent.stopPropagation(event);
      L.DomEvent.preventDefault(event);

      this._draggedMarker = marker;
      this._dragMoved = false;
      this._dragStartPoint = containerPoint;
      this._dragStartMarkerPoint = this._map.latLngToContainerPoint(marker.getLatLng());
      this._dragStartLatLng = marker.getLatLng();
      this._mapWasDragging = this._map.dragging && this._map.dragging.enabled();

      if (this._mapWasDragging) {
        this._map.dragging.disable();
      }

      L.DomEvent.on(document, "mousemove touchmove", this._onPointerMove, this);
      L.DomEvent.on(document, "mouseup touchend touchcancel", this._onPointerUp, this);
    },

    _onPointerMove: function _onPointerMove(event) {
      var marker = this._draggedMarker;
      if (!marker || !this._map) { return; }

      var containerPoint = this._eventToContainerPoint(event);
      var offsetX = containerPoint.x - this._dragStartPoint.x;
      var offsetY = containerPoint.y - this._dragStartPoint.y;

      // Match Leaflet's small click tolerance: a press with a tiny amount of
      // pointer jitter is still a click, not a drag.
      if (!this._dragMoved && Math.sqrt(offsetX * offsetX + offsetY * offsetY) <= 3) {
        return;
      }

      L.DomEvent.preventDefault(event);

      if (!this._dragMoved) {
        this._dragMoved = true;
        marker.closePopup();
        marker.fire("movestart").fire("dragstart");
      }

      var oldLatLng = marker.getLatLng();
      var markerPoint = L.point(
        this._dragStartMarkerPoint.x + offsetX,
        this._dragStartMarkerPoint.y + offsetY
      );
      var latLng = this._map.containerPointToLatLng(markerPoint);

      // Keep the geographic index in sync before redrawing.  RBush removes by
      // bbox first, so it needs the marker's former coordinates.
      this._positionsTree.remove({
        minX: oldLatLng.lng,
        minY: oldLatLng.lat,
        maxX: oldLatLng.lng,
        maxY: oldLatLng.lat,
        marker: marker,
      }, function (a, b) { return a.marker._leaflet_id === b.marker._leaflet_id; });

      marker._latlng = latLng;
      marker.update();

      this._positionsTree.insert({
        minX: latLng.lng,
        minY: latLng.lat,
        maxX: latLng.lng,
        maxY: latLng.lat,
        marker: marker,
      });

      var moveEvent = {
        latlng: latLng,
        oldLatLng: oldLatLng,
        originalEvent: event,
      };
      marker.fire("move", moveEvent).fire("drag", moveEvent);
      this._redraw(true);
    },

    _onPointerUp: function _onPointerUp(event) {
      this._stopDragging(event, true);
    },

    _stopDragging: function _stopDragging(event, fireEvents) {
      var marker = this._draggedMarker;
      if (!marker) { return; }

      L.DomEvent.off(document, "mousemove touchmove", this._onPointerMove, this);
      L.DomEvent.off(document, "mouseup touchend touchcancel", this._onPointerUp, this);

      if (this._mapWasDragging && this._map && this._map.dragging) {
        this._map.dragging.enable();
      }

      if (fireEvents && this._dragMoved) {
        var endPoint = event && this._map
          ? this._eventToContainerPoint(event)
          : this._dragStartPoint;
        var offsetX = endPoint.x - this._dragStartPoint.x;
        var offsetY = endPoint.y - this._dragStartPoint.y;
        var dragEvent = {
          distance: Math.sqrt(offsetX * offsetX + offsetY * offsetY),
          originalEvent: event,
        };

        marker.fire("moveend").fire("dragend", dragEvent);
        this._suppressClick = true;
        clearTimeout(this._clickSuppressTimeout);
        var this$1 = this;
        this._clickSuppressTimeout = setTimeout(function () {
          this$1._suppressClick = false;
          this$1._clickSuppressTimeout = null;
        }, 500);
      }

      this._draggedMarker = null;
      this._dragMoved = false;
      this._dragStartPoint = null;
      this._dragStartMarkerPoint = null;
      this._dragStartLatLng = null;
      this._mapWasDragging = false;
    },

    _onCanvasClick: function _onCanvasClick(event) {
      if (!this._suppressClick) { return; }

      // Browsers dispatch a click after mouseup/touchend.  A completed drag
      // must not turn into a marker (or map) click.
      this._suppressClick = false;
      clearTimeout(this._clickSuppressTimeout);
      this._clickSuppressTimeout = null;
      L.DomEvent.stop(event);
    },

    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *
    //
    // private: event methods
    //
    // * * * * * * * * * * * * * * * * * * * * * * * * * * * *

    _reset: function _reset() {
      var topLeft = this._map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(this._canvas, topLeft);

      var ref = this._map.getSize();
      var x = ref.x;
      var y = ref.y;
      this._canvas.width = x;
      this._canvas.height = y;

      this._redraw();
    },

    _fire: function _fire(event) {
      if (!this._markersTree) { return; }

      if (event.type === "click" && this._suppressClick) {
        this._suppressClick = false;
        clearTimeout(this._clickSuppressTimeout);
        this._clickSuppressTimeout = null;
        return;
      }

      var ref = event.containerPoint;
      var x = ref.x;
      var y = ref.y;
      var marker = this._markerAt({ x: x, y: y });

      if (marker) {
        this._map._container.style.cursor = "pointer";

        if (event.type === "click") {
          if (marker.listens("click")) {
            marker.fire("click");
          }
        }

        if (event.type === "mousemove") {
          if (this._mouseOverMarker && this._mouseOverMarker !== marker) {
            if (this._mouseOverMarker.listens("mouseout")) {
              this._mouseOverMarker.fire("mouseout");
            }
          }

          if (!this._mouseOverMarker || this._mouseOverMarker !== marker) {
            this._mouseOverMarker = marker;
            if (marker.listens("mouseover")) {
              marker.fire("mouseover");
            }
          }
        }
      } else {
        this._map._container.style.cursor = "";
        if (event.type === "mousemove" && this._mouseOverMarker) {
          if (this._mouseOverMarker.listens("mouseout")) {
            this._mouseOverMarker.fire("mouseout");
          }

          delete this._mouseOverMarker;
        }
      }
    },

    _animateZoom: function _animateZoom(event) {
      var scale = this._map.getZoomScale(event.zoom);
      var offset = this._map._latLngBoundsToNewLayerBounds(
        this._map.getBounds(),
        event.zoom,
        event.center
      ).min;

      L.DomUtil.setTransform(this._canvas, offset, scale);
    },
  };

  L.MarkersCanvas = L.Layer.extend(markersCanvas);

